import React, { FC, useEffect, useMemo } from 'react';
import { ContentTransformTypes, ImageElement, VideoElement } from '@bynder-studio/render-core';
import { Dropdown, Form } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { IconCrop } from '@bynder/icons';
import useEditorSelectedElementContentTransform from '~/hooks/useEditorSelectedElementContentTransform';
import useEditorSelectedElement from '~/hooks/useEditorSelectedElement';
import useEditor from '~/hooks/useEditor';
import useForceUpdate from '~/hooks/useForceUpdate';
import { getFreeCropModeParams } from '~/common/editor/helpers';
import { CropModeBaseProps, CropModeProps, TransformOptions, TransformSetting } from './types';
import { CropModeButton, CropModeListItem } from './styled';
import CropSetting from './CropSetting';
import generateTestId from '~/helpers/testIdHelpers';
import useCropModeShortcuts from 'packages/hooks/shortcuts/useCropModeSchortcuts';

const TRANSFORM_OPTOINS: TransformOptions = {
    [ContentTransformTypes.CONTAIN]: {
        value: ContentTransformTypes.CONTAIN,
        label: 'editor.sidebar.shots.crop.mode.contain',
        icon: <IconCrop />,
    },
    [ContentTransformTypes.COVER]: {
        value: ContentTransformTypes.COVER,
        label: 'editor.sidebar.shots.crop.mode.cover',
        icon: <IconCrop />,
    },
    [ContentTransformTypes.COVER_HEIGHT]: {
        value: ContentTransformTypes.COVER_HEIGHT,
        label: 'editor.sidebar.shots.crop.mode.cover-height',
        icon: <IconCrop />,
    },
    [ContentTransformTypes.COVER_WIDTH]: {
        value: ContentTransformTypes.COVER_WIDTH,
        label: 'editor.sidebar.shots.crop.mode.cover-width',
        icon: <IconCrop />,
    },
    [ContentTransformTypes.FREE]: {
        value: ContentTransformTypes.FREE,
        label: 'editor.sidebar.shots.crop.mode.free',
        icon: <IconCrop />,
    },
};

const testIdDropdown = generateTestId('shots_crop_mode__dropdown');
const testIdScale = generateTestId('shots_crop_mode__scale');
const testIdHorizontalOffset = generateTestId('shots_crop_mode__horizontal_offset');
const testIdVerticalOffset = generateTestId('shots_crop_mode__vertical_offset');
const testIdHorizontalAlignment = generateTestId('shots_crop_mode__horizontal_alignment');
const testIdVerticalAlignment = generateTestId('shots_crop_mode__vertical_alignment');
const AUTO_SCALE_MODES = new Set([
    ContentTransformTypes.COVER,
    ContentTransformTypes.CONTAIN,
    ContentTransformTypes.COVER_WIDTH,
    ContentTransformTypes.COVER_HEIGHT,
]);

const CropModeBase: FC<CropModeBaseProps> = ({
    transformType,
    setTransformType,
    translate,
    setSettingValue,
    horizontalAlignment,
    verticalAlignment,
    horizontalOffset,
    minHorizontalOffset,
    maxHorizontalOffset,
    verticalOffset,
    minVerticalOffset,
    maxVerticalOffset,
    scale,
    minScale,
    disabled,
}) => {
    const selectedOption = TRANSFORM_OPTOINS[transformType];
    const isFreeform = selectedOption.value === ContentTransformTypes.FREE;
    const isCoverMode = selectedOption.value === ContentTransformTypes.COVER;

    useCropModeShortcuts({
        toggleMode: () => {
            if (!disabled) {
                if (selectedOption.value === ContentTransformTypes.CONTAIN) {
                    setTransformType(ContentTransformTypes.COVER);
                } else {
                    setTransformType(ContentTransformTypes.CONTAIN);
                }
            }
        },
    });

    const options = Object.values(TRANSFORM_OPTOINS).map(({ label, ...props }) => (
        <Dropdown.Item
            key={label}
            onClick={() => !disabled && setTransformType(ContentTransformTypes[props.value])}
            isChecked={selectedOption.label === label}
            isDisabled={disabled}
        >
            {translate(label)}
        </Dropdown.Item>
    ));

    const defaultCropSettings = (
        <>
            <CropSetting
                label={translate('editor.sidebar.shots.crop.horizontal-align')}
                name="horizontalAlignment"
                format="percent"
                min={-1}
                max={1}
                value={horizontalAlignment}
                setSettingValue={setSettingValue}
                disabled={disabled}
                {...testIdHorizontalAlignment}
            />
            <CropSetting
                label={translate('editor.sidebar.shots.crop.vertical-align')}
                name="verticalAlignment"
                format="percent"
                min={-1}
                max={1}
                value={verticalAlignment}
                setSettingValue={setSettingValue}
                disabled={disabled}
                {...testIdVerticalAlignment}
            />
        </>
    );

    const coverModeOptions = (
        <CropSetting
            label={translate('editor.sidebar.shots.crop.scale')}
            name="scale"
            format="percent"
            min={minScale}
            max={2}
            value={scale}
            isFromCenter={false}
            setSettingValue={setSettingValue}
            disabled={disabled || scale > 2}
            {...testIdScale}
        />
    );
    const freeformModeOptions = (
        <>
            <CropSetting
                label={translate('editor.sidebar.shots.crop.scale')}
                name="scale"
                format="percent"
                min={0}
                max={2}
                value={scale}
                isFromCenter={false}
                setSettingValue={setSettingValue}
                disabled={disabled}
                {...testIdScale}
            />
            <CropSetting
                label={translate('editor.sidebar.shots.crop.horizontal-offset')}
                name="horizontalOffset"
                format="pixels"
                min={minHorizontalOffset}
                max={maxHorizontalOffset}
                value={horizontalOffset}
                setSettingValue={setSettingValue}
                disabled={disabled}
                {...testIdHorizontalOffset}
            />
            <CropSetting
                label={translate('editor.sidebar.shots.crop.vertical-offset')}
                name="verticalOffset"
                format="pixels"
                min={minVerticalOffset}
                max={maxVerticalOffset}
                value={verticalOffset}
                setSettingValue={setSettingValue}
                disabled={disabled}
                {...testIdVerticalOffset}
            />
        </>
    );

    return (
        <>
            <CropModeListItem>
                <Form.Group>
                    <Form.Label>{translate('editor.sidebar.shots.crop.label')}</Form.Label>
                    <Dropdown
                        trigger={({ isOpen, onClick, ...triggerProps }) => (
                            <CropModeButton
                                isPressed={isOpen}
                                rightIcon={<Dropdown.Arrow />}
                                isFullWidth
                                isDisabled={disabled}
                                onClick={!disabled ? onClick : () => {}}
                                {...triggerProps}
                                {...testIdDropdown}
                            >
                                {translate(selectedOption.label)}
                            </CropModeButton>
                        )}
                        position="bottom"
                    >
                        {options}
                    </Dropdown>
                </Form.Group>
            </CropModeListItem>
            {isCoverMode && coverModeOptions}
            {isFreeform && freeformModeOptions}
            {!isFreeform && defaultCropSettings}
        </>
    );
};

const CropMode: FC<CropModeProps> = ({ disabled }) => {
    const { translate } = useTranslate();

    const { creativeModel } = useEditor();
    const { selectedElement } = useEditorSelectedElement<ImageElement | VideoElement>();

    // todo: types, error handling
    const { contentTransform } = useEditorSelectedElementContentTransform();
    const { id, locked } = selectedElement;

    // Force re-render when element dimensions change so all dimension-dependent
    // values (minScale, currentScale, offset bounds) are recalculated.
    const forceUpdate = useForceUpdate();

    useEffect(() => {
        if (!creativeModel) {
            return;
        }

        return creativeModel.onElementUpdate(id, forceUpdate);
    }, [creativeModel, id, forceUpdate]);

    // Read fresh values from selectedElement on every render (it's a mutable object)
    const { dimension } = selectedElement;
    const { width: naturalWidth, height: naturalHeight } = selectedElement.naturalDimension;
    const coverScale = Math.max(dimension.width / naturalWidth, dimension.height / naturalHeight);
    const containScale = Math.min(dimension.width / naturalWidth, dimension.height / naturalHeight);
    const coverWidthScale = dimension.width / naturalWidth;
    const coverHeightScale = dimension.height / naturalHeight;

    const getModeDerivedScale = (mode: ContentTransformTypes) => {
        if (mode === ContentTransformTypes.COVER) {
            return coverScale;
        }

        if (mode === ContentTransformTypes.CONTAIN) {
            return containScale;
        }

        if (mode === ContentTransformTypes.COVER_WIDTH) {
            return coverWidthScale;
        }

        if (mode === ContentTransformTypes.COVER_HEIGHT) {
            return coverHeightScale;
        }

        return undefined;
    };

    const isFreeMode = (mode: ContentTransformTypes) => mode === ContentTransformTypes.FREE;
    const isAutoScaleMode = (mode: ContentTransformTypes) => AUTO_SCALE_MODES.has(mode);

    const hasValidManualScale = (transform = contentTransform) => {
        if (isFreeMode(transform.type)) {
            return transform.scale != null;
        }

        if ('hasManualScale' in transform) {
            return transform.hasManualScale();
        }

        return typeof transform.scale === 'number' && transform.scale > 0;
    };

    const setTransformType = (newValue: ContentTransformTypes) => {
        const { scale, ...restTransform } = contentTransform;
        const keepScale = isFreeMode(newValue);
        const modeScale = getModeDerivedScale(newValue);
        const params = {
            contentTransform: {
                ...restTransform,
                ...(modeScale != null ? { scale: modeScale } : {}),
                ...(keepScale && scale != null ? { scale } : {}),
                ...(isFreeMode(newValue) ? getFreeCropModeParams(selectedElement) : {}),
                type: newValue,
            },
        };

        creativeModel.updateElement(id, params);
    };

    const setSettingValue = (newValue: number, setting: TransformSetting) => {
        const newTransform = { ...contentTransform };
        const { scale, horizontalOffset = 0, verticalOffset = 0 } = contentTransform;

        switch (setting) {
            case 'horizontalAlignment':
            case 'verticalAlignment':
                newTransform[setting] = newValue;

                if (!isFreeMode(contentTransform.type)) {
                    const hasManualScaleInCurrentMode = hasValidManualScale(contentTransform);

                    newTransform.scale = hasManualScaleInCurrentMode
                        ? currentScale
                        : (getModeDerivedScale(contentTransform.type) ?? currentScale);
                }

                break;
            case 'horizontalOffset':
            case 'verticalOffset':
                newTransform[setting] = newValue;
                break;
            case 'scale':
                // Enforce minimum scale for COVER mode - can't go below initial cover scale
                if (contentTransform.type === ContentTransformTypes.COVER) {
                    if (newValue < coverScale) {
                        newValue = coverScale;
                    }
                }

                // For COVER/CONTAIN modes, offsets are calculated from alignment by render-core
                // So we only set the scale and let calculateBoxCrop handle positioning
                if (isAutoScaleMode(contentTransform.type)) {
                    newTransform.scale = newValue;
                } else {
                    // For FREE mode, scale from current position
                    // Use the actual current scale value
                    const currentScale = scale;
                    newTransform.horizontalOffset = Math.round(
                        horizontalOffset + (naturalWidth * currentScale - naturalWidth * newValue) / 2,
                    );
                    newTransform.verticalOffset = Math.round(
                        verticalOffset + (naturalHeight * currentScale - naturalHeight * newValue) / 2,
                    );
                    newTransform.scale = newValue;
                }

                break;
            default:
        }

        creativeModel.updateElement(id, { contentTransform: newTransform });
    };

    const minScale = useMemo(() => {
        return getModeDerivedScale(contentTransform.type) ?? 0;
    }, [contentTransform.type, coverScale, containScale, coverWidthScale, coverHeightScale]);

    // When element is resized, persist scale adjustments for auto-scale modes
    // so the canvas keeps the expected crop behavior.
    useEffect(() => {
        if (!creativeModel || id == null) {
            return;
        }

        // Read directly from the mutable selectedElement for the freshest value
        const ct = selectedElement.contentTransform;

        if (isFreeMode(ct.type)) {
            return;
        }

        const modeDerivedScale = getModeDerivedScale(ct.type);

        if (modeDerivedScale == null) {
            return;
        }

        if (ct.type !== ContentTransformTypes.COVER) {
            if (ct.scale !== modeDerivedScale) {
                creativeModel.updateElement(id, {
                    contentTransform: { ...ct, scale: modeDerivedScale },
                });
            }

            return;
        }

        // Only clamp if the user has explicitly set a scale value.
        // When scale is null/undefined, render-core calculates cover scale internally.
        if (ct.scale != null && (ct.scale < minScale || ct.scale > 2)) {
            creativeModel.updateElement(id, {
                contentTransform: { ...ct, scale: minScale },
            });
        }
    }, [creativeModel, id, selectedElement, minScale, coverScale, containScale, coverWidthScale, coverHeightScale]);

    // Calculate current scale properly based on mode
    const hasManualScale = hasValidManualScale(contentTransform);

    let currentScale = hasManualScale
        ? (contentTransform.scale ?? 1)
        : (getModeDerivedScale(contentTransform.type) ?? 1);

    if (currentScale < minScale || currentScale > 2) {
        currentScale = minScale;
    }

    return (
        <CropModeBase
            transformType={contentTransform.type}
            setTransformType={setTransformType}
            verticalAlignment={contentTransform?.verticalAlignment ?? 0.5}
            horizontalAlignment={contentTransform?.horizontalAlignment ?? 0.5}
            scale={currentScale}
            minScale={minScale}
            verticalOffset={contentTransform?.verticalOffset ?? 0}
            minVerticalOffset={Math.floor(-1 * currentScale * naturalHeight)}
            maxVerticalOffset={Math.ceil(dimension.height)}
            horizontalOffset={contentTransform?.horizontalOffset ?? 0}
            minHorizontalOffset={Math.floor(-1 * currentScale * naturalWidth)}
            maxHorizontalOffset={Math.ceil(dimension.width)}
            setSettingValue={setSettingValue}
            disabled={locked || disabled}
            translate={translate}
        />
    );
};

export default CropMode;
